import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import { useFieldArray, useWatch } from 'react-hook-form'
import { useEffect, useState } from 'react'
import styles from './HFInputs.module.sass'
import { RequisitionStatus } from 'utils/enums'
import HFMedicalActsProtocolRow from './HFMedicalActsProtocolRow'
import { v4 as uuidv4 } from 'uuid'

const PHYSIOTHERAPY_CODE = '90.99.00.01'

export const REQUESTED_APPROVED_STATES = [
	RequisitionStatus.Requested,
	RequisitionStatus.Executed,
	RequisitionStatus.AdministrativeApprove,
	RequisitionStatus.PartiallyApproved,
	RequisitionStatus.AcceptedByProvider,
	RequisitionStatus.OnGoing,
	RequisitionStatus.PendingExecution,
	RequisitionStatus.WaitingForSchedule,
]

const HFMedicalActsProtocol = (props) => {
	const {
		name,
		data,
		blockCountDispatcher,
		onJustificationChanged,
		validateRef,
		showWarning,
	} = props
	const { i18n } = useGlobal()

	const {
		medicalActs,
		requisitions,
		processId,
		accidentType,
		processPostalCode,
		onUpdateFormData,
		requestingProviderId,
		appointmentDate,
		scheduleDate,
		insuranceId,
		requisitionStateId,
	} = data

	const { injuredBirthday, injuredType, bodyPart } = props

	const { fields, append, remove } = useFieldArray({
		name,
		keyName: 'key',
	})
	const protocolMedicalActs = useWatch({ name })

	const requisitionIds = data.requisitions?.map((item) => item.id)

	const [suggestionsLog, setSuggestionsLog] = useState(() => {
		const log = {}
		if (protocolMedicalActs?.length) {
			protocolMedicalActs.forEach((act) => {
				if (act.medicalActId) {
					log[String(act.medicalActId)] = {
						actId: act.medicalActId,
						providerId: act.serviceProviderId ?? null,
						timestamp: new Date().toISOString(),
						sent: true,
					}
				}
			})
		}
		return log
	})

	useEffect(() => {
		if (props.onSuggestionsChange) {
			props.onSuggestionsChange(suggestionsLog)
		}
	}, [suggestionsLog])

	const handleSuggestion = (newActId, suggestionObj, prevActId) => {
		setSuggestionsLog((prev) => {
			const copy = { ...prev }

			if (prevActId && copy[prevActId]) {
				delete copy[prevActId]
			}

			if (newActId) {
				copy[String(newActId)] = suggestionObj
			}

			return copy
		})
	}

	return (
		<>
			<HStack gap='small' columns={['75px', 4, 4]}>
				<small>
					<strong>{i18n`Qtd.`}</strong>
				</small>
				<small>
					<strong>{i18n`Medical Acts`}</strong>
				</small>
			</HStack>

			{fields.map(({ key }, index) => {
				const medicalActId = protocolMedicalActs[index]?.medicalActId
				const medicalActCode = medicalActs
					?.find(
						(ma) =>
							parseInt(ma.value, 10) ===
							parseInt(medicalActId, 10)
					)
					?.text?.split(' ')?.[0]

				let totalRequisitionsForMedicalAct = 0
				if (medicalActCode === PHYSIOTHERAPY_CODE) {
					totalRequisitionsForMedicalAct = requisitions.reduce(
						(acc, r) => {
							if (
								!REQUESTED_APPROVED_STATES.includes(
									r.status?.id
								)
							)
								return acc
							const reqMedicalAct = r.medicalActs?.find(
								(ma) => ma.code === PHYSIOTHERAPY_CODE
							)
							if (!reqMedicalAct) return acc
							acc +=
								reqMedicalAct?.executedQuantity ||
								reqMedicalAct?.approvedQuantity
							return acc
						},
						0
					)
				} else {
					totalRequisitionsForMedicalAct =
						requisitions?.filter((r) => {
							const reqMedicalAct = r.medicalActs?.find(
								(ma) => ma.code === medicalActCode
							)
							if (
								!reqMedicalAct ||
								reqMedicalAct?.approvedQuantity === 0
							)
								return false
							if (
								!REQUESTED_APPROVED_STATES.includes(
									r.status?.id
								)
							)
								return false
							return true
						}).length ?? 0
				}

				return (
					<HFMedicalActsProtocolRow
						key={key}
						name={name}
						index={index}
						processId={processId}
						totalRequisitionsForMedicalAct={
							totalRequisitionsForMedicalAct
						}
						medicalActs={medicalActs}
						providerEntities={data.providerEntities}
						accidentType={accidentType}
						postalCode={processPostalCode}
						remove={remove}
						insuranceId={insuranceId}
						onUpdateFormData={onUpdateFormData}
						requisitionIds={requisitionIds}
						onSuggestion={handleSuggestion}
						injuredBirthday={injuredBirthday?.split('T')[0]}
						injuredType={injuredType}
						bodyPart={bodyPart}
						requestingProviderId={requestingProviderId}
						appointmentDate={appointmentDate}
						scheduleDate={scheduleDate}
						blockCountDispatcher={blockCountDispatcher}
						onJustificationChanged={onJustificationChanged}
						validateRef={validateRef}
						showWarning={showWarning}
						requisitionStateId={requisitionStateId}
					/>
				)
			})}

			<ActionButton
				className={styles['add-appointment-protocol']}
				secondary
				onClick={(e) => {
					e.preventDefault()
					append({
						uuid: uuidv4(),
						quantity: 1,
						medicalActId: '',
						medicalActCode: '',
						serviceProviderId: '',
						sent: false,
					})
				}}
			>
				<Icon name='plus' height='14px' width='14px' />
				<small>{i18n`Add`}</small>
			</ActionButton>
		</>
	)
}

export default HFMedicalActsProtocol
